import { z } from 'zod';
import { idSchema, isoDateTimeSchema, tenantIdSchema } from './common.schema';
import { TENANT_ROLES, GLOBAL_ROLES } from '../constants/roles';
/**
 * User — profile + tenant memberships. Identity itself lives in the shared
 * `meshtek-auth-{env}` Firebase Auth project (auth IA §14); this document
 * carries Detect's authorization state. Document id = Firebase Auth uid.
 *
 * A membership grant may be scoped to specific clients within the tenant
 * (client-group scoping, tech-arch §3): `clientIds: null` = all clients.
 */
export const tenantMembershipSchema = z
    .object({
    tenantId: tenantIdSchema,
    role: z.enum(TENANT_ROLES),
    /** null = unrestricted (all clients in the tenant). A scoped grant
     *  must name at least one client — `[]` would be a dead membership.
     *  Scopes are SETS: duplicates would distort scoped pagination. */
    clientIds: z
        .array(idSchema)
        .min(1)
        .max(500)
        .nullable()
        .refine((ids) => ids === null || new Set(ids).size === ids.length, {
        message: 'clientIds must not contain duplicates',
    }),
    joinedAt: isoDateTimeSchema,
})
    .strict();
export const userSchema = z
    .object({
    email: z.string().email(),
    displayName: z.string().min(1).max(200),
    globalRole: z.enum(GLOBAL_ROLES),
    tenants: z.array(tenantMembershipSchema).max(200),
    /** Denormalized mirror of tenants[].tenantId — Firestore cannot
     *  query into arrays of objects, so member listing uses
     *  `where('tenantIds', 'array-contains', tenantId)`. Kept consistent
     *  by the superRefine below. */
    tenantIds: z.array(tenantIdSchema).max(200),
    /** FCM registration tokens for push delivery. */
    fcmTokens: z.array(z.string().max(512)).max(20),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
    lastSeenAt: isoDateTimeSchema.nullable(),
    disabledAt: isoDateTimeSchema.nullable(),
})
    .strict()
    // Auth-critical: role resolution is keyed by tenantId, so duplicate
    // memberships for the same tenant would make it ambiguous. Reject.
    // The denormalized tenantIds mirror must also agree exactly.
    .superRefine((val, ctx) => {
    const seen = new Set();
    for (const m of val.tenants) {
        if (seen.has(m.tenantId)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['tenants'],
                message: `duplicate membership for tenant "${m.tenantId}"`,
            });
        }
        seen.add(m.tenantId);
    }
    const mirror = new Set(val.tenantIds);
    if (mirror.size !== val.tenantIds.length ||
        mirror.size !== seen.size ||
        [...seen].some((t) => !mirror.has(t))) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['tenantIds'],
            message: 'tenantIds must exactly mirror tenants[].tenantId',
        });
    }
});
/** Admin API input: grant/change a user's membership in the admin's tenant. */
export const membershipInputSchema = z
    .object({
    userId: idSchema,
    role: z.enum(TENANT_ROLES),
    /** null = unrestricted; a scoped grant must name at least one client;
     *  duplicates rejected (scopes are sets). */
    clientIds: z
        .array(idSchema)
        .min(1)
        .max(500)
        .nullable()
        .default(null)
        .refine((ids) => ids === null || new Set(ids).size === ids.length, {
        message: 'clientIds must not contain duplicates',
    }),
})
    .strict();
/** Self-service profile update. */
export const userProfileUpdateSchema = z
    .object({
    displayName: z.string().min(1).max(200).optional(),
})
    .strict();
//# sourceMappingURL=user.schema.js.map